import { useCallback, useState } from 'react';
import { Upload, FileImage, CheckCircle, X } from 'lucide-react';
import { cn } from '@/utils';
import api from '@/services/api';
import toast from '@/hooks/useToast';
import { Prescription } from '@/types';

interface Props {
  onUploaded: (prescription: Prescription) => void;
  patientNotes?: string;
  className?: string;
}

export const PrescriptionUploader = ({ onUploaded, patientNotes, className }: Props) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState<Prescription | null>(null);

  const handleUpload = async (file: File) => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Invalid file type', 'Please upload a JPEG or PNG image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large', 'Maximum file size is 5MB.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('prescription', file);
    if (patientNotes) formData.append('patientNotes', patientNotes);

    try {
      const res = await api.post('/prescriptions/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploaded(res.data.data);
      onUploaded(res.data.data);
      toast.success('Prescription uploaded!', 'Our pharmacist will review it shortly.');
    } catch {
      toast.error('Upload failed', 'Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, []);

  if (uploaded) {
    return (
      <div className={cn('border-2 border-success rounded-xl p-6 bg-green-50 flex items-center gap-4', className)}>
        <CheckCircle className="h-10 w-10 text-success shrink-0" />
        <div>
          <p className="font-semibold text-gray-800">Prescription uploaded!</p>
          <p className="text-sm text-gray-500">Awaiting pharmacist review</p>
          <img src={uploaded.imageUrl} alt="Prescription" className="mt-2 h-20 rounded-lg object-cover" />
        </div>
        <button onClick={() => setUploaded(null)} className="ml-auto text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <label
      className={cn(
        'flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 cursor-pointer transition-all',
        dragging ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50',
        uploading && 'pointer-events-none opacity-50',
        className
      )}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <input type="file" className="hidden" accept="image/*" onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} />
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full" />
          <p className="text-sm text-gray-500">Uploading...</p>
        </div>
      ) : (
        <>
          <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center mb-3">
            <FileImage className="h-7 w-7 text-primary-400" />
          </div>
          <p className="font-semibold text-gray-800 mb-1">Upload Prescription</p>
          <p className="text-sm text-gray-500 text-center">Drag & drop or click to select (JPEG/PNG, max 5MB)</p>
        </>
      )}
    </label>
  );
};
